import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { Button, Modal, Select, InputNumber, Spin, Empty, Input } from "antd";
import { toast } from "../../components/toasts/toastStore";
import { apiComprasInstance, authHeaderCompras } from "../../redux/actions/compras/compras";

import DevolucionesProveedor from "../../components/devoluciones/DevolucionesProveedor";

import "./EventoDetallePage.css";

dayjs.locale("es");

const fmtFechaCorta = (v) => {
  if (!v) return "—";
  const d = dayjs(v);
  return d.isValid() ? d.format("D MMM YYYY, HH:mm") : "—";
};

const fmtDatetime = (v) => {
  if (!v) return "—";
  const d = dayjs(v);
  return d.isValid() ? d.format("DD/MM/YYYY, HH:mm") : "—";
};

const money = (n) => (n == null ? "—" : `$${Number(n).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

const ESTATUS_LABEL = { pendiente: "Pendiente", parcial: "Pago parcial", pagada: "Pagada" };

const ACTION_CONFIG = {
  CREATE: { label: "Creacion", color: "#15803d", bg: "#dcfce7" },
  UPDATE: { label: "Actualizacion", color: "#f90336", bg: "#fee2e6" },
  DELETE: { label: "Cancelacion", color: "#b91c1c", bg: "#fee2e2" },
};
const getActionConfig = (a) => ACTION_CONFIG[a] || { label: a || "Cambio", color: "#595c5e", bg: "#f1f5f9" };

export default function CompraDetallePage() {
  const { idCompra } = useParams();
  const navigate = useNavigate();

  const [compra, setCompra] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("datos");
  const [cancelando, setCancelando] = useState(false);

  const [formasPago, setFormasPago] = useState([]);
  const [addPagoOpen, setAddPagoOpen] = useState(false);
  const [savingPago, setSavingPago] = useState(false);
  const [nuevoPago, setNuevoPago] = useState({});

  const [actividad, setActividad] = useState([]);
  const [loadingActividad, setLoadingActividad] = useState(false);

  const fetchCompra = useCallback(() => {
    setLoading(true);
    apiComprasInstance.get(`/compras/${idCompra}`, { headers: authHeaderCompras() })
      .then((res) => setCompra(res.data))
      .catch(() => toast.error("No se pudo cargar la compra"))
      .finally(() => setLoading(false));
  }, [idCompra]);

  useEffect(() => { fetchCompra(); }, [fetchCompra]);

  useEffect(() => {
    apiComprasInstance.get("/formas-pago", { headers: authHeaderCompras() })
      .then((r) => setFormasPago((r.data?.items ?? []).map((f) => ({ label: f.nombre, value: f.id_forma_pago }))))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (activeTab !== "actividad") return;
    setLoadingActividad(true);
    apiComprasInstance.get(`/compras/${idCompra}/actividad`, { headers: authHeaderCompras() })
      .then((res) => setActividad(res.data || []))
      .catch(() => {})
      .finally(() => setLoadingActividad(false));
  }, [activeTab, idCompra]);

  const handleAddPago = async () => {
    if (!nuevoPago.id_forma_pago || nuevoPago.monto == null) {
      toast.warning("Selecciona la forma de pago e ingresa el monto");
      return;
    }
    setSavingPago(true);
    try {
      await apiComprasInstance.post(`/compras/${idCompra}/pagos`, nuevoPago, { headers: authHeaderCompras() });
      toast.success("Pago registrado");
      setAddPagoOpen(false);
      setNuevoPago({});
      fetchCompra();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo registrar el pago");
    } finally {
      setSavingPago(false);
    }
  };

  const handleCancelar = () => {
    let motivo = "";
    Modal.confirm({
      title: "Cancelar esta compra?",
      content: (
        <div>
          <p>Esta accion revierte la existencia agregada por esta compra. No se puede deshacer.</p>
          <Input.TextArea rows={2} placeholder="Motivo de cancelacion" onChange={(e) => { motivo = e.target.value; }} />
        </div>
      ),
      okText: "Cancelar compra",
      okButtonProps: { danger: true },
      cancelText: "Volver",
      onOk: async () => {
        if (!motivo.trim()) {
          toast.warning("Escribe un motivo de cancelacion");
          return Promise.reject();
        }
        setCancelando(true);
        try {
          await apiComprasInstance.post(`/compras/${idCompra}/cancelar`, { motivo }, { headers: authHeaderCompras() });
          toast.success("Compra cancelada");
          fetchCompra();
        } catch (err) {
          toast.error(err?.response?.data?.detail || "No se pudo cancelar");
        } finally {
          setCancelando(false);
        }
      },
    });
  };

  if (loading) {
    return (
      <div className="cd-main" style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 400 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!compra) {
    return (
      <div className="cd-main" style={{ padding: 40 }}>
        <Empty description="Compra no encontrada" />
      </div>
    );
  }

  const totalPagado = (compra.pagos || []).reduce((a, p) => a + parseFloat(p.monto || 0), 0);

  return (
    <div className="cd-main">
      <div className="cd-content">
        <div className="cd-header-card">
          <div className="cd-header-top">
            <div>
              <div className="cd-header-name-row">
                <h1 className="cd-client-name">COMPRA #{compra.id_compra} - {(compra.proveedor_nombre || "").toUpperCase()}</h1>
              </div>
              <div className="cd-header-meta">
                <span className="cd-meta-item"><i className="fa-solid fa-calendar" /> Registrada el {fmtFechaCorta(compra.datetime)}</span>
                {compra.creado_por && <span className="cd-meta-item"><i className="fa-solid fa-user" /> {compra.creado_por}</span>}
              </div>
            </div>
            <div className="cd-header-actions">
              {!compra.cancelada && (
                <Button icon={<i className="fa-solid fa-ban" />} className="cd-btn-delete" loading={cancelando} onClick={handleCancelar}>
                  Cancelar compra
                </Button>
              )}
            </div>
          </div>

          <div className="cd-header-tabs">
            <button className={`cd-tab-btn ${activeTab === "datos" ? "cd-tab-btn-active" : ""}`} onClick={() => setActiveTab("datos")}>
              <i className="fa-solid fa-file-lines" /> Informacion
            </button>
            <button className={`cd-tab-btn ${activeTab === "productos" ? "cd-tab-btn-active" : ""}`} onClick={() => setActiveTab("productos")}>
              <i className="fa-solid fa-list" /> Productos comprados ({(compra.detalle || []).length})
            </button>
            <button className={`cd-tab-btn ${activeTab === "pagos" ? "cd-tab-btn-active" : ""}`} onClick={() => setActiveTab("pagos")}>
              <i className="fa-solid fa-money-bill-wave" /> Pagos ({(compra.pagos || []).length})
            </button>
            <button className={`cd-tab-btn ${activeTab === "devoluciones" ? "cd-tab-btn-active" : ""}`} onClick={() => setActiveTab("devoluciones")}>
              <i className="fa-solid fa-rotate-left" /> Devoluciones
            </button>
            <button className={`cd-tab-btn ${activeTab === "actividad" ? "cd-tab-btn-active" : ""}`} onClick={() => setActiveTab("actividad")}>
              <i className="fa-solid fa-clock" /> Actividad
            </button>
          </div>
        </div>

        {compra.cancelada === 1 && (
          <div className="cd-card" style={{ background: "#fee2e2", border: "1px solid #fecaca", marginBottom: 16 }}>
            <strong style={{ color: "#b91c1c" }}>Esta compra fue cancelada.</strong>
            <div style={{ marginTop: 6, color: "#7f1d1d" }}>{compra.motivo_cancelacion}</div>
            <div style={{ marginTop: 4, fontSize: 12, color: "#991b1b" }}>
              Por {compra.cancelado_por || "—"} el {fmtDatetime(compra.fecha_cancelacion)}
            </div>
          </div>
        )}

        {activeTab === "datos" && (
          <div className="cd-bento-grid" style={{ gridTemplateColumns: "2fr 1fr" }}>
            <div className="cd-card cd-card-client">
              <div className="cd-card-header">
                <div className="cd-card-icon-wrap"><i className="fa-solid fa-cart-arrow-down"></i></div>
                <h2 className="cd-card-title">Informacion de la compra</h2>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px 32px" }}>
                <div><span className="cd-field-label">Proveedor</span><span className="cd-field-value">{compra.proveedor_nombre}</span></div>
                <div><span className="cd-field-label">Folio de factura</span><span className="cd-field-value">{compra.folio_factura || "—"}</span></div>
                <div><span className="cd-field-label">Comprobante fiscal</span><span className="cd-field-value">{compra.tiene_comprobante_fiscal ? "Si" : "No"}</span></div>
                <div><span className="cd-field-label">Fecha de vencimiento</span><span className="cd-field-value">{compra.fecha_vencimiento || "—"}</span></div>
                <div><span className="cd-field-label">Estatus de pago</span><span className="cd-field-value">{ESTATUS_LABEL[compra.estatus_nombre] || compra.estatus_nombre}</span></div>
                <div><span className="cd-field-label">Fecha de registro</span><span className="cd-field-value">{fmtFechaCorta(compra.datetime)}</span></div>
              </div>
            </div>

            <div className="cd-card">
              <div className="cd-card-header">
                <div className="cd-card-icon-wrap"><i className="fa-solid fa-coins"></i></div>
                <h2 className="cd-card-title">Totales</h2>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <div><span className="cd-field-label">Subtotal</span><span className="cd-field-value">{money(compra.subtotal)}</span></div>
                <div><span className="cd-field-label">Impuesto</span><span className="cd-field-value">{money(compra.impuesto)}</span></div>
                <div><span className="cd-field-label">Total</span><span className="cd-field-value" style={{ fontSize: 18 }}>{money(compra.total)}</span></div>
                <div><span className="cd-field-label">Pagado</span><span className="cd-field-value" style={{ color: "#15803d" }}>{money(totalPagado)}</span></div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "productos" && (
          <div className="cd-card">
            <div className="cd-card-header">
              <div className="cd-card-icon-wrap"><i className="fa-solid fa-list"></i></div>
              <h2 className="cd-card-title">Productos comprados</h2>
            </div>
            {(compra.detalle || []).length === 0 ? (
              <Empty description="Sin productos" />
            ) : (
              <div className="cd-pagos-list">
                {compra.detalle.map((d) => (
                  <div key={d.id_compra_detalle} style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr", gap: 12, padding: "12px 4px", borderBottom: "1px solid #f0edf2" }}>
                    <div><span className="cd-field-label">Producto</span><div className="cd-field-value">{d.producto_nombre} ({d.clave})</div></div>
                    <div><span className="cd-field-label">Cantidad</span><div className="cd-field-value">{d.cantidad}</div></div>
                    <div><span className="cd-field-label">Costo unitario</span><div className="cd-field-value">{money(d.costo_unitario_subtotal)}</div></div>
                    <div><span className="cd-field-label">Impuesto unitario</span><div className="cd-field-value">{money(d.costo_unitario_impuesto)}</div></div>
                    <div><span className="cd-field-label">Importe</span><div className="cd-field-value">{money((parseFloat(d.cantidad) * (parseFloat(d.costo_unitario_subtotal) + parseFloat(d.costo_unitario_impuesto || 0))).toFixed(2))}</div></div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "pagos" && (
          <div className="cd-card">
            <div className="cd-card-header" style={{ justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div className="cd-card-icon-wrap"><i className="fa-solid fa-money-bill-wave"></i></div>
                <h2 className="cd-card-title">Pagos</h2>
              </div>
              {!compra.cancelada && compra.estatus_nombre !== "pagada" && (
                <Button type="primary" icon={<i className="fa-solid fa-plus" />} onClick={() => setAddPagoOpen(true)} style={{ backgroundColor: "#f90336", borderColor: "#f90336" }}>
                  Registrar pago
                </Button>
              )}
            </div>
            {(compra.pagos || []).length === 0 ? (
              <Empty description="Todavia no se han registrado pagos" />
            ) : (
              <div className="cd-pagos-list">
                {compra.pagos.map((p) => (
                  <div key={p.id_compra_pago} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, padding: "12px 4px", borderBottom: "1px solid #f0edf2" }}>
                    <div><span className="cd-field-label">Forma de pago</span><div className="cd-field-value">{p.forma_pago_nombre}</div></div>
                    <div><span className="cd-field-label">Monto</span><div className="cd-field-value">{money(p.monto)}</div></div>
                    <div><span className="cd-field-label">Fecha</span><div className="cd-field-value">{fmtDatetime(p.datetime)}</div></div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "devoluciones" && <DevolucionesProveedor compra={compra} />}

        {activeTab === "actividad" && (
          <div className="cd-card">
            <div className="cd-card-header">
              <div className="cd-card-icon-wrap"><i className="fa-solid fa-clock"></i></div>
              <h2 className="cd-card-title">Actividad</h2>
              <span className="cd-actividad-count">{actividad.length}</span>
            </div>
            {loadingActividad ? <Spin /> : (
              <div className="cd-actividad-list">
                {actividad.map((item, idx) => {
                  const cfg = getActionConfig(item.action);
                  return (
                    <div key={item.id_audit_log || idx} className="cd-actividad-item">
                      <div className="cd-actividad-dot-col">
                        <div className="cd-actividad-dot" style={{ background: cfg.color }} />
                        {idx < actividad.length - 1 && <div className="cd-actividad-line" />}
                      </div>
                      <div className="cd-actividad-body">
                        <div className="cd-actividad-top-row">
                          <span className="cd-actividad-badge" style={{ color: cfg.color, background: cfg.bg }}>{cfg.label}</span>
                          <span className="cd-actividad-message">{item.message}</span>
                        </div>
                        <div className="cd-actividad-bottom-row">
                          <span className="cd-actividad-time">{fmtDatetime(item.datetime)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      <Modal
        open={addPagoOpen}
        onCancel={() => setAddPagoOpen(false)}
        onOk={handleAddPago}
        okText="Registrar pago"
        cancelText="Cancelar"
        title="Registrar pago"
        confirmLoading={savingPago}
        centered
        okButtonProps={{ style: { background: "#f90336", borderColor: "#f90336" } }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <Select placeholder="Forma de pago" options={formasPago} value={nuevoPago.id_forma_pago} onChange={(v) => setNuevoPago((f) => ({ ...f, id_forma_pago: v }))} />
          <InputNumber style={{ width: "100%" }} placeholder="Monto" min={0} precision={2} value={nuevoPago.monto} onChange={(v) => setNuevoPago((f) => ({ ...f, monto: v }))} />
        </div>
      </Modal>
    </div>
  );
}
